// frontend/src/pages/freelancer/FreelancerProfileEdit.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import axios from "axios";
import { prepareImageForUpload } from "../../utils/imageUploadPolicy";
import ImageUploadField from "../../components/ImageUploadField";
import "./freelancerStyles.css";

function FreelancerProfileEdit() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const isBoothRenterRoute = location.pathname.startsWith(
    "/dashboard/booth-renter",
  );
  const basePath = isBoothRenterRoute
    ? "/dashboard/booth-renter"
    : "/dashboard/freelancer";

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "", // Read-only
    bio: "",
    // Never pre-select a zone: an R4/R7 must actively choose one. Starts empty until
    // the loaded record supplies the stored value.
    timezone: "",
  });

  // Professional types (categories) manager - R4/R7
  const [professionalTypes, setProfessionalTypes] = useState([]);
  const [primaryProfessionalType, setPrimaryProfessionalType] = useState("");
  const [availableProfessionalTypes, setAvailableProfessionalTypes] = useState(
    [],
  );

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/freelancer/profile`, {
        headers,
      });

      // Populate form with existing data
      setFormData({
        firstName: res.data.user.firstName || "",
        lastName: res.data.user.lastName || "",
        phone: res.data.user.phone || "",
        email: res.data.user.email || "",
        bio: res.data.staff?.bio || "",
        // The stored value is the R4/R7's own chosen zone; empty when none is stored.
        timezone: res.data.staff?.timezone || "",
      });

      // Professional types (categories) manager
      setProfessionalTypes(res.data.staff?.professionalTypes || []);
      setPrimaryProfessionalType(
        res.data.staff?.primaryProfessionalType ||
          res.data.staff?.professionalTypes?.[0] ||
          "",
      );

      // Set existing profile picture if available
      if (res.data.user.profilePicture) {
        setProfilePicturePreview(res.data.user.profilePicture);
      }

      // Load available professional types to choose from
      try {
        const ptRes = await axios.get(
          `${baseURL}/api/platform-settings/professional-types/public`,
        );
        setAvailableProfessionalTypes(ptRes.data.professionalTypes || []);
      } catch (ptErr) {
      }
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg("Unable to load profile. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear messages on input change
    setErrorMsg("");
    setSuccessMsg("");
  };

  const addProfessionalType = (value) => {
    setProfessionalTypes((prev) =>
      prev.includes(value) ? prev : [...prev, value],
    );
    setPrimaryProfessionalType((prev) => prev || value);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const removeProfessionalType = (value) => {
    setProfessionalTypes((prev) => prev.filter((t) => t !== value));
    setPrimaryProfessionalType((prev) => (prev === value ? "" : prev));
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setErrorMsg("Please select an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMsg("Image size must be less than 5MB");
        return;
      }

      setProfilePicture(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);

      setErrorMsg("");
    }
  };

  const uploadProfilePicture = async () => {
    if (!profilePicture) return null;

    try {
      setUploadingImage(true);
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return null;

      const formData = new FormData();
      formData.append("profilePicture", await prepareImageForUpload(profilePicture));

      const res = await axios.post(
        `${baseURL}/api/staff/upload-profile-picture`,
        formData,
        {
          headers: {
            ...headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return res.data.profilePictureUrl;
    } catch (err) {
      throw new Error("Failed to upload profile picture");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Never submit without an actively chosen timezone (browser guard, not backend).
    if (!formData.timezone || !formData.timezone.trim()) {
      setErrorMsg("Please choose your timezone.");
      return;
    }

    setErrorMsg("");
    setSuccessMsg("");
    setSaving(true);

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) {
        setSaving(false);
        return;
      }

      // Validate inputs
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setErrorMsg("First name and last name are required");
        setSaving(false);
        return;
      }

      // Upload profile picture if changed
      let profilePictureUrl = profilePicturePreview;
      if (profilePicture) {
        profilePictureUrl = await uploadProfilePicture();
      }

      // Send update request
      await axios.patch(
        `${baseURL}/api/staff/profile`,
        {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          phone: formData.phone.trim(),
          profilePicture: profilePictureUrl,
          professionalTypes,
          primaryProfessionalType,
          timezone: formData.timezone,
        },
        { headers },
      );

      setSuccessMsg("Profile updated successfully!");

      // Redirect back to profile view after 1.5 seconds
      setTimeout(() => {
        navigate(`${basePath}/profile`);
      }, 1500);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        const errorMessage =
          err?.response?.data?.message ||
          err.message ||
          "Unable to update profile. Please try again.";
        setErrorMsg(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`${basePath}/profile`);
  };

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading profile...</p>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  return (
    <FreelancerSidebarLayout>
      <div style={{ maxWidth: "600px", margin: "0 auto", padding: "20px" }}>
        {/* Back to Profile Button */}
        <button
          onClick={() => navigate(`${basePath}/profile`)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            marginBottom: "20px",
            border: "1px solid #ddd",
            borderRadius: "8px",
            background: "#fff",
            color: "#333",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          <span>←</span> Back to Profile
        </button>

        {/* Header */}
        <div style={{ marginBottom: "30px" }}>
          <h2 style={{ margin: "0 0 8px 0" }}>Edit Profile</h2>
          <p style={{ margin: 0, color: "#666" }}>
            Update your personal information
          </p>
        </div>

        {/* Error Message */}
        {errorMsg && (
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
          </div>
        )}

        {/* Success Message */}
        {successMsg && (
          <div
            style={{
              background: "#e6ffed",
              border: "1px solid #03543f",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#03543f" }}>{successMsg}</p>
          </div>
        )}

        {/* Form */}
        <div className="dashboard-card" style={{ padding: "30px" }}>
          <form onSubmit={handleSubmit}>
            {/* Profile Picture Upload */}
            {/* One shared field: input + validation + HEIC + preview + remove + guidance */}
            <ImageUploadField
              label="Profile Picture"
              variant="avatar"
              uploadOnSelect={false}
              localFiles={profilePicture ? [profilePicture] : []}
              onSelectFiles={(file) => setProfilePicture(file || null)}
              onError={(message) => setErrorMsg(message)}
            />

            {/* First Name */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                First Name <span style={{ color: "#c00" }}>*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                }}
                placeholder="Enter your first name"
              />
            </div>

            {/* Last Name */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Last Name <span style={{ color: "#c00" }}>*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                }}
                placeholder="Enter your last name"
              />
            </div>

            {/* Phone */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                }}
                placeholder="(555) 123-4567"
              />
            </div>

            {/* Professional Types / Categories (R4/R7) */}
            <div
              style={{
                marginBottom: "20px",
                padding: "14px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                background: "#f8fafc",
              }}
            >
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "4px",
                  color: "#001d4a",
                }}
              >
                Professional Types / Categories
              </label>
              <p style={{ fontSize: "12px", color: "#666", margin: "0 0 10px 0" }}>
                Your specialties unlock the service categories you can offer. You
                can add more at any time.
              </p>
              {professionalTypes.length === 0 ? (
                <p style={{ fontSize: "13px", color: "#b91c1c" }}>
                  Select at least one type below.
                </p>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginBottom: "10px",
                  }}
                >
                  {professionalTypes.map((t) => {
                    const meta = availableProfessionalTypes.find(
                      (a) => a.value === t,
                    );
                    const label = meta?.label || t;
                    const isPrimary = primaryProfessionalType === t;
                    return (
                      <span
                        key={t}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 10px",
                          borderRadius: "16px",
                          background: isPrimary ? "#001d4a" : "#e2e8f0",
                          color: isPrimary ? "#ffffff" : "#0f172a",
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                      >
                        {label}
                        {!isPrimary && (
                          <button
                            type="button"
                            onClick={() => setPrimaryProfessionalType(t)}
                            title="Make primary"
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              fontSize: "12px",
                              color: "#64748b",
                              padding: 0,
                            }}
                          >
                            ★
                          </button>
                        )}
                        {isPrimary && (
                          <span style={{ fontSize: "11px", opacity: 0.85 }}>
                            primary
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeProfessionalType(t)}
                          aria-label={`Remove ${label}`}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "14px",
                            lineHeight: 1,
                            color: isPrimary ? "#ffffff" : "#64748b",
                            padding: 0,
                          }}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <select
                value=""
                onChange={(e) =>
                  e.target.value && addProfessionalType(e.target.value)
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "14px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  background: "#fff",
                }}
              >
                <option value="">Select a professional type to add…</option>
                {availableProfessionalTypes
                  .filter((a) => !professionalTypes.includes(a.value))
                  .map((a) => (
                    <option key={a.value} value={a.value}>
                      {a.label}
                    </option>
                  ))}
              </select>
              <p style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                Add as many as you need. The one marked{" "}
                <strong>primary</strong> is shown first on your profile.
              </p>
            </div>

            {/* Bio */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                About Me
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                maxLength={500}
                rows={4}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
                placeholder="Tell clients about your experience, specialties, and what makes you unique..."
              />
              <p
                style={{ margin: "5px 0 0 0", fontSize: "12px", color: "#666" }}
              >
                {formData.bio.length}/500 characters
              </p>
            </div>

            {/* Timezone */}
            <div style={{ marginBottom: "30px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Timezone
              </label>
              <select
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                required
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                }}
              >
                <option value="">Select your timezone...</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Phoenix">Arizona Time (MST)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
              </select>
              <p
                style={{
                  margin: "5px 0 0 0",
                  fontSize: "12px",
                  color: "#666",
                }}
              >
                Your schedule and bookings will use this timezone.
              </p>
            </div>

            {/* Email (Read-only) */}
            <div style={{ marginBottom: "30px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                  color: "#001d4a",
                }}
              >
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                disabled
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  fontSize: "16px",
                  background: "#f5f5f5",
                  color: "#666",
                  cursor: "not-allowed",
                  boxSizing: "border-box",
                }}
              />
              <p
                style={{
                  margin: "5px 0 0 0",
                  fontSize: "12px",
                  color: "#666",
                }}
              >
                Email cannot be changed. Contact support if needed.
              </p>
            </div>

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                gap: "15px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                style={{
                  padding: "12px 30px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  background: "#fff",
                  color: "#333",
                  fontSize: "16px",
                  fontWeight: "500",
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary"
                style={{
                  padding: "12px 30px",
                  minWidth: "120px",
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerProfileEdit;
